import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type {
    AgenteMonedaView,
    ClienteDTO,
    CotizacionDTO,
    CotizacionItemDTO,
    CotizacionStatus,
    CotizacionTemplateDTO,
    CreateCotizacionItemRequest,
    CreateCotizacionRequest,
    FormaPago,
    Genero,
    PagaebleResult,
    Parentesco,
    ProspectoDTO,
    SearchCotizacionTemplateDTO,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { daysFromToday, formatSpokenDate, shiftIso, todayIso } from '../../../utils/dates'
import { clip, onlyLetters } from '../../../utils/inputs'
import { lookupName, searchClientes, searchProspectos, useLookupField } from '../../../utils/lookups'
import { formatAmount, parseAmount, sanitizeDecimal } from '../../../utils/numbers'
import { useRowDrag } from '../../../utils/row-drag'
import {
    hasIncompleteValues,
    moveToIndex,
    nextRowKey,
    patchByKey,
    removeByKey,
    toValueRequests,
    toValueRows,
} from '../../../utils/value-rows'
import type { KeyedRow, ValueRow } from '../../../utils/value-rows'
import {
    aseguradoraLabel,
    errorMessage,
    fetchCoberturaSuggestions,
    fetchQuoteCatalogs,
    FORMA_PAGO,
    formatPlanAmount,
    insurerOption,
    isPlanAmountField,
    PLAN_MAX,
    PLAN_VALUE_MAX,
    planValueFields,
    QUOTE_VIEWER_PATH,
    QUOTES_PATH,
    ramoFields,
} from '../common/quote-common'
import type { PlanValueField, QuoteInsurerOption, QuoteLocationState } from '../common/quote-common'

const LOAD_ERROR = 'No se pudo cargar la cotización.'
const CATALOGS_ERROR = 'No se pudieron cargar los catálogos.'
const TEMPLATE_ERROR = 'No se pudo cargar el template.'
const SAVE_ERROR = 'Error al guardar la cotización'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const TOAST_MS = 4500
const SUGGESTION_LIMIT = 6

export const TEXTO_MAX = 1500
export const NOMBRE_ASEGURADO_MAX = 200
export const VEHICULO_MAX = 100

export { GENEROS, PARENTESCOS } from '../../../utils/catalogs'

export type ContratanteTipo = 'cliente' | 'prospecto'

/** Cliente o prospecto seleccionable como contratante. */
export interface QuoteContratante {
    id: number
    key: string
    tipo: ContratanteTipo
    nombres: string | null
    apPat: string | null
    apMat: string | null
    email: string | null
}

export interface QuoteInsuredRow extends KeyedRow {
    id: number | null
    nombre: string
    parentesco: Parentesco | ''
    genero: Genero | ''
    fecNacimiento: string
    edad: string
}

export interface QuotePlanRow extends KeyedRow {
    id: number | null
    formaPago: FormaPago | ''
    primaPrimerRecibo: string
    primaReciboSub: string
    total: string
}

export type PlanAmountField = 'primaPrimerRecibo' | 'primaReciboSub' | 'total'

export interface QuoteItemForm extends KeyedRow {
    id: number | null
    templateId: number | ''
    aseguradora: QuoteInsurerOption | null
    plan: string
    sumaAsegurada: string
    deducible: string
    coaseguro: string
    topeCoaseguro: string
    copago: string
    caracteristicas: ValueRow[]
    coberturas: ValueRow[]
    planesPago: QuotePlanRow[]
}

export interface QuoteFormState {
    id: number | null
    uuid: string | null
    noCotizacion: number | null
    ramo: string
    contratante: QuoteContratante | null
    correo: string
    fecVencimiento: string
    status: CotizacionStatus
    /** Abreviación (`MXN`); el API la guarda en cada propuesta, aquí aplica a todas. */
    moneda: string
    texto: string
    vehiculo: { id: number | null; descripcion: string; modelo: string }
    asegurados: QuoteInsuredRow[]
    items: QuoteItemForm[]
}

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

export type ValueListKind = 'caracteristicas' | 'coberturas'

export type ProposalTab = ValueListKind | 'planes'

interface UndoSnapshot {
    items?: QuoteItemForm[]
    asegurados?: QuoteInsuredRow[]
}

interface Toast {
    message: string
    undo: UndoSnapshot | null
}

const contratanteFromCliente = (cliente: Pick<ClienteDTO, 'id' | 'nombres' | 'apPat' | 'apMat'>, email: string | null = null): QuoteContratante => ({
    id: cliente.id,
    key: `cliente-${cliente.id}`,
    tipo: 'cliente',
    nombres: cliente.nombres,
    apPat: cliente.apPat,
    apMat: cliente.apMat,
    email,
})

const contratanteFromProspecto = (prospecto: ProspectoDTO): QuoteContratante => ({
    id: prospecto.id,
    key: `prospecto-${prospecto.id}`,
    tipo: 'prospecto',
    nombres: prospecto.nombres,
    apPat: prospecto.apPat,
    apMat: prospecto.apMat,
    email: prospecto.email,
})

const searchContratantes = async (text: string): Promise<QuoteContratante[]> => {
    const [clientes, prospectos] = await Promise.all([
        searchClientes(text).catch(() => [] as ClienteDTO[]),
        searchProspectos(text).catch(() => [] as ProspectoDTO[]),
    ])
    return [...clientes.map((item) => contratanteFromCliente(item)), ...prospectos.map(contratanteFromProspecto)]
}

const newPlanRow = (formaPago: FormaPago | '' = ''): QuotePlanRow => ({ key: nextRowKey(), id: null, formaPago, primaPrimerRecibo: '', primaReciboSub: '', total: '' })

const newItem = (): QuoteItemForm => ({
    key: nextRowKey(),
    id: null,
    templateId: '',
    aseguradora: null,
    plan: '',
    sumaAsegurada: '',
    deducible: '',
    coaseguro: '',
    topeCoaseguro: '',
    copago: '',
    caracteristicas: [],
    coberturas: [],
    planesPago: [newPlanRow('contado')],
})

/** Copia con llaves nuevas y sin `id`, para que el API la cree como otra propuesta. */
const cloneItem = (item: QuoteItemForm): QuoteItemForm => ({
    ...item,
    key: nextRowKey(),
    id: null,
    templateId: '',
    caracteristicas: item.caracteristicas.map((row) => ({ ...row, key: nextRowKey(), id: null })),
    coberturas: item.coberturas.map((row) => ({ ...row, key: nextRowKey(), id: null })),
    planesPago: item.planesPago.map((row) => ({ ...row, key: nextRowKey(), id: null })),
})

const newAsegurado = (titular: boolean): QuoteInsuredRow => ({
    key: nextRowKey(),
    id: null,
    nombre: '',
    parentesco: titular ? 'titular' : '',
    genero: '',
    fecNacimiento: '',
    edad: '',
})

const emptyState = (): QuoteFormState => ({
    id: null,
    uuid: null,
    noCotizacion: null,
    ramo: '',
    contratante: null,
    correo: '',
    fecVencimiento: shiftIso(7),
    status: 'captura',
    moneda: '',
    texto: '',
    vehiculo: { id: null, descripcion: '', modelo: '' },
    asegurados: [],
    items: [],
})

const amountText = (value?: number | null) => (value ? formatAmount(value) : '')

const toItemForm = (item: CotizacionItemDTO): QuoteItemForm => ({
    key: nextRowKey(),
    id: item.id,
    templateId: '',
    aseguradora: insurerOption(item.aseguradora),
    plan: item.plan || '',
    sumaAsegurada: formatPlanAmount(item.sumaAsegurada),
    deducible: formatPlanAmount(item.deducible),
    coaseguro: formatPlanAmount(item.coaseguro),
    topeCoaseguro: item.topeCoaseguro || '',
    copago: item.copago || '',
    caracteristicas: toValueRows(item.caracteristicas),
    coberturas: toValueRows(item.coberturas),
    planesPago: (item.planesPago || []).map((plan) => ({
        key: nextRowKey(),
        id: plan.id,
        formaPago: plan.formaPago || '',
        primaPrimerRecibo: amountText(plan.primaPrimerRecibo),
        primaReciboSub: amountText(plan.primaReciboSub),
        total: amountText(plan.total),
    })),
})

const toFormState = (body: CotizacionDTO): QuoteFormState => {
    const vehiculo = body.vehiculos?.[0]
    return {
        id: body.id,
        uuid: body.uuid,
        noCotizacion: body.noCotizacion,
        ramo: body.ramo || '',
        contratante: body.cliente
            ? contratanteFromCliente(body.cliente)
            : body.prospecto ? contratanteFromProspecto(body.prospecto) : null,
        correo: body.correo || '',
        fecVencimiento: body.fecVencimiento || '',
        status: body.status || 'captura',
        moneda: body.items?.find((item) => item.moneda)?.moneda || '',
        texto: body.texto || '',
        vehiculo: { id: vehiculo?.id ?? null, descripcion: vehiculo?.descripcion || '', modelo: vehiculo?.modelo ? String(vehiculo.modelo) : '' },
        asegurados: [...(body.asegurados || [])]
            .sort((left, right) => Number(left.orden ?? 0) - Number(right.orden ?? 0))
            .map((item) => ({
                key: nextRowKey(),
                id: item.id,
                nombre: item.nombre || '',
                parentesco: item.parentesco || '',
                genero: item.genero || '',
                fecNacimiento: item.fecNacimiento || '',
                edad: item.edad != null ? String(item.edad) : '',
            })),
        items: (body.items || []).map(toItemForm),
    }
}

const parcialidades = (formaPago: FormaPago | '') => FORMA_PAGO.find((item) => item.key === formaPago)?.parcialidades || 1

const formaLabel = (formaPago: FormaPago | '') => FORMA_PAGO.find((item) => item.key === formaPago)?.label || ''

const roundAmount = (value: number) => Math.round(value * 100) / 100

const planTotal = (plan: QuotePlanRow) => {
    if (plan.formaPago === 'contado') return plan.total
    const total = parseAmount(plan.primaPrimerRecibo) + ((parcialidades(plan.formaPago) - 1) * parseAmount(plan.primaReciboSub))
    return total ? formatAmount(roundAmount(total)) : ''
}

/** `MXN $9,840`: la abreviación va al frente porque una cotización puede no estar en pesos. */
const quoteMoney = (value: number, moneda: string) => {
    const digits = Number.isInteger(value) ? 0 : 2
    return `${moneda ? `${moneda} ` : ''}$${value.toLocaleString('es-MX', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
}

const toItemRequest = (item: QuoteItemForm, ramo: string, moneda: string): CreateCotizacionItemRequest => {
    const fields = ramoFields(ramo)
    return {
        id: item.id,
        aseguradora: Number(item.aseguradora?.id),
        plan: item.plan.trim(),
        moneda,
        sumaAsegurada: formatPlanAmount(item.sumaAsegurada),
        deducible: fields.deducible ? formatPlanAmount(item.deducible) : '',
        coaseguro: fields.salud ? formatPlanAmount(item.coaseguro) : '',
        topeCoaseguro: fields.salud ? item.topeCoaseguro.trim() : '',
        copago: fields.salud ? item.copago.trim() : '',
        caracteristicas: fields.caracteristicas ? toValueRequests(item.caracteristicas) : [],
        coberturas: toValueRequests(item.coberturas),
        planesPago: item.planesPago.map((plan) => ({
            id: plan.id,
            formaPago: plan.formaPago as FormaPago,
            total: parseAmount(plan.total),
            primaPrimerRecibo: plan.formaPago === 'contado' ? null : parseAmount(plan.primaPrimerRecibo),
            primaReciboSub: plan.formaPago === 'contado' ? null : parseAmount(plan.primaReciboSub),
        })),
    }
}

/** Campo con error → texto corto para el resumen ("propuesta 1: aseguradora"). */
export type FieldErrors = Record<string, string>

export interface QuoteSteps {
    /** Texto corto bajo cada punto del stepper. */
    labels: string[]
    /** Título de la tarjeta y de la lista de avance del resumen. */
    titles: string[]
    hints: string[]
}

export const LAST_STEP = 2

const detailStep = (ramo: string) => {
    const fields = ramoFields(ramo)
    if (fields.asegurados) return { label: 'Asegurados', hint: 'Titular y dependientes a cotizar' }
    if (fields.vehiculo) return { label: 'Vehículo', hint: 'Descripción y modelo del vehículo' }
    if (fields.bien) return { label: 'Bien asegurado', hint: 'Detalle del bien que se va a asegurar' }
    return { label: 'Detalle', hint: 'Primero elige el ramo de la cotización' }
}

export const quoteSteps = (ramo: string): QuoteSteps => {
    const detail = detailStep(ramo)
    return {
        labels: ['Datos generales', detail.label, 'Propuestas'],
        titles: ['Datos generales', detail.label, 'Propuestas'],
        hints: [
            'Ramo, cliente o prospecto, correo y vencimiento',
            detail.hint,
            'Aseguradora, plan, coberturas y planes de pago de cada propuesta',
        ],
    }
}

/** Errores de una propuesta con textos cortos ("aseguradora", "plan 2: primer recibo"). */
const itemIssues = (item: QuoteItemForm, i: number, ramo: string): FieldErrors => {
    const e: FieldErrors = {}
    const fields = ramoFields(ramo)
    if (!item.aseguradora?.id) e[`i${i}aseguradora`] = 'aseguradora'
    if (!item.plan.trim()) e[`i${i}plan`] = 'plan'
    if (!item.sumaAsegurada.trim()) e[`i${i}sumaAsegurada`] = 'suma asegurada'
    if (fields.caracteristicas && hasIncompleteValues(item.caracteristicas)) e[`i${i}caracteristicas`] = 'características incompletas'
    if (hasIncompleteValues(item.coberturas)) e[`i${i}coberturas`] = 'coberturas incompletas'
    if (item.planesPago.length === 0) e[`i${i}planes`] = 'plan de pago'
    item.planesPago.forEach((plan, j) => {
        const label = item.planesPago.length > 1 ? `plan ${j + 1}: ` : ''
        if (!plan.formaPago) {
            e[`p${i}-${j}formaPago`] = `${label}forma de pago`
            return
        }
        if (plan.formaPago !== 'contado') {
            if (!parseAmount(plan.primaPrimerRecibo)) e[`p${i}-${j}primaPrimerRecibo`] = `${label}primer recibo`
            if (!parseAmount(plan.primaReciboSub)) e[`p${i}-${j}primaReciboSub`] = `${label}subsecuentes`
        } else if (!parseAmount(plan.total)) {
            e[`p${i}-${j}total`] = `${label}prima total`
        }
    })
    return e
}

const sectionErrors = (state: QuoteFormState, sec: number): FieldErrors => {
    const e: FieldErrors = {}
    const fields = ramoFields(state.ramo)
    if (sec === 0) {
        if (!state.ramo) e.ramo = 'ramo'
        if (!state.contratante) e.contratante = 'cliente o prospecto'
        if (!EMAIL_PATTERN.test(state.correo.trim())) e.correo = 'correo válido'
        if (!state.moneda) e.moneda = 'moneda'
        if (!state.fecVencimiento || (!state.id && (daysFromToday(state.fecVencimiento) ?? 0) < 0)) e.fecVencimiento = 'vencimiento'
    }
    if (sec === 1) {
        if (fields.bien && !state.texto.trim()) e.texto = 'detalle del bien asegurado'
        if (fields.vehiculo) {
            if (!state.vehiculo.descripcion.trim()) e.vDescripcion = 'descripción del vehículo'
            if (state.vehiculo.modelo.length !== 4) e.vModelo = 'modelo (año) del vehículo'
        }
        if (fields.asegurados) {
            state.asegurados.forEach((row, i) => {
                const label = `asegurado ${i + 1}`
                if (!row.nombre.trim()) e[`a${i}nombre`] = `${label}: nombre`
                if (!row.parentesco) e[`a${i}parentesco`] = `${label}: parentesco`
                if (!row.genero) e[`a${i}genero`] = `${label}: género`
            })
        }
    }
    if (sec === 2) {
        if (state.items.length === 0) e.items = 'al menos una propuesta'
        state.items.forEach((item, i) => {
            Object.entries(itemIssues(item, i, state.ramo)).forEach(([key, label]) => {
                e[key] = `propuesta ${i + 1}: ${label}`
            })
        })
    }
    return e
}

const cheapestPlan = (item: QuoteItemForm) => {
    const totals = item.planesPago.map((plan) => parseAmount(plan.total)).filter((total) => total > 0)
    return totals.length ? Math.min(...totals) : null
}

const planSummary = (plan: QuotePlanRow, moneda: string) => {
    if (!plan.formaPago) return 'Elige la forma de pago'
    if (plan.formaPago === 'contado') return parseAmount(plan.total) ? 'Pago único' : 'Escribe la prima total'
    const pagos = parcialidades(plan.formaPago)
    const primer = parseAmount(plan.primaPrimerRecibo)
    const sub = parseAmount(plan.primaReciboSub)
    if (!primer || !sub) return `${pagos} recibos · captura los montos`
    if (primer === sub) return `${pagos} recibos de ${quoteMoney(sub, moneda)}`
    return `1.º de ${quoteMoney(primer, moneda)} y ${pagos - 1} de ${quoteMoney(sub, moneda)}`
}

/** El pago fraccionado normalmente cuesta más que el anual; si sale más barato suele ser un error de captura. */
const planWarning = (item: QuoteItemForm, moneda: string) => {
    const anual = item.planesPago.find((plan) => plan.formaPago === 'contado' && parseAmount(plan.total))
    if (!anual) return null
    const anualTotal = parseAmount(anual.total)
    const raro = item.planesPago.find((plan) => plan.formaPago && plan.formaPago !== 'contado' && parseAmount(plan.total) && parseAmount(plan.total) < anualTotal)
    if (!raro) return null
    return `Revisa: el pago ${formaLabel(raro.formaPago).toLowerCase()} (${quoteMoney(parseAmount(raro.total), moneda)}) sale más barato que el anual (${quoteMoney(anualTotal, moneda)}). Normalmente el fraccionado cuesta más.`
}

export const useQuoteFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { cotizacionId } = useParams()
    const backUrl = (location.state as QuoteLocationState | null)?.backUrl || QUOTES_PATH
    const correoRequest = useRef(0)
    const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    /** Llave del renglón vacío al final de cada lista; se conserva al convertirlo en renglón real para no perder el foco. */
    const draftKeys = useRef<Record<string, string>>({})
    const drag = useRowDrag()

    const [state, setState] = useState<QuoteFormState>(emptyState)
    const [aseguradoras, setAseguradoras] = useState<QuoteInsurerOption[]>([])
    const [monedas, setMonedas] = useState<AgenteMonedaView[]>([])
    const [templates, setTemplates] = useState<SearchCotizacionTemplateDTO[]>([])
    const [coberturaSuggestions, setCoberturaSuggestions] = useState<string[]>([])
    const [correoOptions, setCorreoOptions] = useState<string[]>([])
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [contratanteMenu, setContratanteMenu] = useState<HTMLElement | null>(null)
    const [newContratante, setNewContratante] = useState<ContratanteTipo | null>(null)
    const [step, setStep] = useState(0)
    const [maxStep, setMaxStep] = useState(cotizacionId ? LAST_STEP : 0)
    const [tried, setTried] = useState(false)
    const [triedSec, setTriedSec] = useState<Record<number, boolean>>({})
    const [openKey, setOpenKey] = useState<string | null>(null)
    const [tabs, setTabs] = useState<Record<string, ProposalTab>>({})
    const [toast, setToast] = useState<Toast | null>(null)

    const patch = (partial: Partial<QuoteFormState>) => setState((current) => ({ ...current, ...partial }))

    const patchItem = (key: string, partial: Partial<QuoteItemForm>) =>
        setState((current) => ({ ...current, items: patchByKey(current.items, key, partial) }))

    const updateItem = (key: string, update: (item: QuoteItemForm) => Partial<QuoteItemForm>) =>
        setState((current) => ({ ...current, items: current.items.map((item) => (item.key === key ? { ...item, ...update(item) } : item)) }))

    const showToast = (text: string, undo: UndoSnapshot | null = null) => {
        clearTimeout(toastTimer.current)
        setToast({ message: text, undo })
        toastTimer.current = setTimeout(() => setToast(null), TOAST_MS)
    }

    useEffect(() => () => clearTimeout(toastTimer.current), [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setMessage(null)
                const catalogs = await fetchQuoteCatalogs()
                if (!active) return
                setAseguradoras(catalogs.aseguradoras.map((item) => insurerOption(item)).filter((item): item is QuoteInsurerOption => Boolean(item)))
                setMonedas(catalogs.monedas)
                if (!cotizacionId) {
                    const preferred = catalogs.monedas.find((item) => item.abreviacion === 'MXN') || catalogs.monedas[0]
                    setState((current) => (current.moneda || !preferred?.abreviacion ? current : { ...current, moneda: preferred.abreviacion }))
                    return
                }
                const response = await APIInvoke.invokeGET(`/cotizaciones/${cotizacionId}`)
                const payload: WrapperResponse<CotizacionDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                const loaded = toFormState(payload.body)
                setState(loaded)
                setOpenKey(loaded.items[0]?.key || null)
                if (payload.body.cliente?.id) loadCorreos(payload.body.cliente.id, false)
            } catch (error) {
                if (active) setMessage({ type: 'error', message: errorMessage(error, cotizacionId ? LOAD_ERROR : CATALOGS_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cotizacionId])

    useEffect(() => {
        if (!state.ramo) {
            setTemplates([])
            setCoberturaSuggestions([])
            return undefined
        }
        let active = true
        const load = async () => {
            const [suggestions, templatesPayload] = await Promise.all([
                fetchCoberturaSuggestions(state.ramo).catch(() => []),
                APIInvoke.invokeGET(`/cotizaciones/templates?ramo=${encodeURIComponent(state.ramo)}&size=100&order=nombre%20asc`)
                    .then((response: Response) => response.json() as Promise<WrapperResponse<PagaebleResult<SearchCotizacionTemplateDTO>> | null>)
                    .catch(() => null),
            ])
            if (!active) return
            setCoberturaSuggestions(suggestions)
            setTemplates(templatesPayload?.ok ? templatesPayload.body?.items || [] : [])
        }
        load()
        return () => { active = false }
    }, [state.ramo])

    const loadCorreos = async (clienteId: number, fillCorreo: boolean) => {
        const request = ++correoRequest.current
        try {
            const response = await APIInvoke.invokeGET(`/clientes/${clienteId}`)
            const payload: WrapperResponse<ClienteDTO> | null = await response.json()
            if (request !== correoRequest.current || !payload?.ok) return
            const correos = (payload.body?.correos || []).map((item) => item.correo || '').filter(Boolean)
            setCorreoOptions(correos)
            if (fillCorreo && correos[0]) patch({ correo: correos[0] })
        } catch {
            if (request === correoRequest.current) setCorreoOptions([])
        }
    }

    const changeContratante = (contratante: QuoteContratante | null) => {
        correoRequest.current += 1
        setCorreoOptions(contratante?.email ? [contratante.email] : [])
        patch({ contratante, ...(contratante?.email ? { correo: contratante.email } : {}) })
        if (contratante?.tipo === 'cliente') loadCorreos(contratante.id, true)
    }

    const contratanteLookup = useLookupField<QuoteContratante>({ value: state.contratante, onChange: changeContratante, search: searchContratantes })

    const changeRamo = (ramo: string) => {
        if (state.id) return
        const first = newItem()
        setState((current) => ({
            ...current,
            ramo,
            texto: '',
            vehiculo: { id: null, descripcion: '', modelo: '' },
            asegurados: ramoFields(ramo).asegurados ? [newAsegurado(true)] : [],
            items: [first],
        }))
        setOpenKey(first.key)
    }

    const applyTemplate = async (itemKey: string, templateId: number | '') => {
        patchItem(itemKey, { templateId })
        if (!templateId) return
        try {
            setLoading(true)
            const response = await APIInvoke.invokeGET(`/cotizaciones/templates/${templateId}`)
            const payload: WrapperResponse<CotizacionTemplateDTO> | null = await response.json()
            if (!payload?.ok || !payload.body) throw new Error(payload?.message || TEMPLATE_ERROR)
            const template = payload.body
            patchItem(itemKey, {
                templateId,
                aseguradora: insurerOption(template.aseguradora),
                plan: template.plan || '',
                sumaAsegurada: formatPlanAmount(template.sumaAsegurada),
                deducible: formatPlanAmount(template.deducible),
                coaseguro: formatPlanAmount(template.coaseguro),
                topeCoaseguro: template.topeCoaseguro || '',
                copago: template.copago || '',
                caracteristicas: toValueRows(template.caracteristicas, false),
                coberturas: toValueRows(template.coberturas, false),
            })
        } catch (error) {
            setMessage({ type: 'error', message: errorMessage(error, TEMPLATE_ERROR) })
        } finally {
            setLoading(false)
        }
    }

    const patchAsegurado = (key: string, partial: Partial<QuoteInsuredRow>) =>
        setState((current) => ({ ...current, asegurados: patchByKey(current.asegurados, key, partial) }))

    const asegurado = {
        add: () => setState((current) => ({ ...current, asegurados: [...current.asegurados, newAsegurado(current.asegurados.length === 0)] })),
        remove: (key: string) => {
            showToast('Asegurado quitado', { asegurados: state.asegurados })
            setState((current) => {
                const asegurados = removeByKey(current.asegurados, key)
                return { ...current, asegurados: asegurados.map((row, index) => (index === 0 ? { ...row, parentesco: 'titular' as Parentesco } : row)) }
            })
        },
        /** El titular siempre queda en el primer lugar. */
        moveTo: (from: number, to: number) => {
            if (from === 0 || to === 0) return
            setState((current) => ({ ...current, asegurados: moveToIndex(current.asegurados, from, to) }))
        },
        nombre: (key: string, value: string) => patchAsegurado(key, { nombre: onlyLetters(value, NOMBRE_ASEGURADO_MAX) }),
        parentesco: (key: string, value: Parentesco | '') => patchAsegurado(key, { parentesco: value }),
        genero: (key: string, value: Genero | '') => patchAsegurado(key, { genero: value }),
        edad: (key: string, value: string) => patchAsegurado(key, { edad: value.replace(/\D/g, '').slice(0, 3) }),
    }

    const draftKey = (itemKey: string, kind: ValueListKind) => {
        const id = `${itemKey}:${kind}`
        if (!draftKeys.current[id]) draftKeys.current[id] = nextRowKey()
        return draftKeys.current[id]
    }

    const appendValues = (itemKey: string, kind: ValueListKind, rows: Array<Pick<ValueRow, 'descripcion' | 'valor'>>) =>
        updateItem(itemKey, (row) => ({ [kind]: [...row[kind], ...rows.map((value) => ({ key: nextRowKey(), id: null, ...value }))] }))

    const valueList = (itemKey: string, kind: ValueListKind) => ({
        draftKey: draftKey(itemKey, kind),
        change: (rowKey: string, partial: Partial<Pick<ValueRow, 'descripcion' | 'valor'>>) =>
            updateItem(itemKey, (row) => ({ [kind]: patchByKey<ValueRow>(row[kind], rowKey, partial) })),
        /** Escribir en el renglón vacío lo vuelve renglón real con la misma llave y deja otro vacío al final. */
        changeDraft: (partial: Partial<Pick<ValueRow, 'descripcion' | 'valor'>>) => {
            const key = draftKey(itemKey, kind)
            draftKeys.current[`${itemKey}:${kind}`] = nextRowKey()
            updateItem(itemKey, (row) => ({ [kind]: [...row[kind], { key, id: null, descripcion: '', valor: '', ...partial }] }))
        },
        remove: (rowKey: string) => {
            showToast('Renglón quitado', { items: state.items })
            updateItem(itemKey, (row) => ({ [kind]: removeByKey(row[kind], rowKey) }))
        },
        moveTo: (from: number, to: number) => updateItem(itemKey, (row) => ({ [kind]: moveToIndex(row[kind], from, to) })),
    })

    const updatePlan = (itemKey: string, planKey: string, update: (plan: QuotePlanRow) => QuotePlanRow) =>
        updateItem(itemKey, (row) => ({ planesPago: row.planesPago.map((plan) => (plan.key === planKey ? update(plan) : plan)) }))

    const planPago = {
        add: (itemKey: string) => updateItem(itemKey, (row) => {
            const usadas = row.planesPago.map((plan) => plan.formaPago)
            const forma = FORMA_PAGO.find((option) => !usadas.includes(option.key))?.key || ''
            return { planesPago: [...row.planesPago, newPlanRow(forma)] }
        }),
        remove: (itemKey: string, planKey: string) => {
            showToast('Plan de pago quitado', { items: state.items })
            updateItem(itemKey, (row) => ({ planesPago: removeByKey(row.planesPago, planKey) }))
        },
        moveTo: (itemKey: string, from: number, to: number) => updateItem(itemKey, (row) => ({ planesPago: moveToIndex(row.planesPago, from, to) })),
        formaPago: (itemKey: string, planKey: string, formaPago: FormaPago | '') =>
            updatePlan(itemKey, planKey, (plan) => ({ ...plan, formaPago, primaPrimerRecibo: '', primaReciboSub: '', total: '' })),
        amount: (itemKey: string, planKey: string, field: PlanAmountField, value: string) =>
            updatePlan(itemKey, planKey, (plan) => {
                const next = { ...plan, [field]: sanitizeDecimal(value) }
                return { ...next, total: planTotal(next) }
            }),
        amountBlur: (itemKey: string, planKey: string, field: PlanAmountField) =>
            updatePlan(itemKey, planKey, (plan) => ({ ...plan, [field]: formatPlanAmount(plan[field]) })),
    }

    const item = {
        add: () => {
            const next = newItem()
            setState((current) => ({ ...current, items: [...current.items, next] }))
            setOpenKey(next.key)
        },
        remove: (key: string) => {
            const index = state.items.findIndex((row) => row.key === key)
            showToast(`Propuesta ${index + 1} quitada`, { items: state.items })
            setState((current) => ({ ...current, items: removeByKey(current.items, key) }))
        },
        duplicate: (key: string) => {
            const index = state.items.findIndex((row) => row.key === key)
            if (index < 0) return
            const copy = cloneItem(state.items[index])
            setState((current) => ({ ...current, items: [...current.items, copy] }))
            setOpenKey(copy.key)
            showToast(`Propuesta ${index + 1} duplicada`)
        },
        toggle: (key: string) => setOpenKey((current) => (current === key ? null : key)),
        tab: (key: string, tab: ProposalTab) => setTabs((current) => ({ ...current, [key]: tab })),
        template: applyTemplate,
        aseguradora: (key: string, aseguradora: QuoteInsurerOption | null) => patchItem(key, { aseguradora }),
        plan: (key: string, value: string) => patchItem(key, { plan: clip(value, PLAN_MAX) }),
        field: (key: string, field: PlanValueField, value: string) =>
            patchItem(key, { [field]: isPlanAmountField(field) ? sanitizeDecimal(value) : clip(value, PLAN_VALUE_MAX) }),
        fieldBlur: (key: string, field: PlanValueField) => {
            if (!isPlanAmountField(field)) return
            updateItem(key, (row) => ({ [field]: formatPlanAmount(row[field]) }))
        },
        addSuggestion: (key: string, descripcion: string) => appendValues(key, 'coberturas', [{ descripcion, valor: '' }]),
        /** Agrega las coberturas de otra propuesta que esta aún no tiene. */
        copyCoberturas: (key: string, fromKey: string) => {
            const source = state.items.find((row) => row.key === fromKey)
            const target = state.items.find((row) => row.key === key)
            if (!source || !target) return
            const existing = new Set(target.coberturas.map((row) => row.descripcion.trim().toLowerCase()))
            appendValues(key, 'coberturas', source.coberturas
                .filter((row) => row.descripcion.trim() && !existing.has(row.descripcion.trim().toLowerCase()))
                .map((row) => ({ descripcion: row.descripcion, valor: row.valor })))
            showToast('Coberturas copiadas')
        },
        valueList,
    }

    const change = {
        ramo: changeRamo,
        contratante: changeContratante,
        correo: (value: string) => patch({ correo: clip(value, 200) }),
        fecVencimiento: (value: string) => patch({ fecVencimiento: value }),
        status: (value: CotizacionStatus) => patch({ status: value }),
        moneda: (value: string) => patch({ moneda: value }),
        texto: (value: string) => patch({ texto: clip(value, TEXTO_MAX) }),
        vehiculoDescripcion: (value: string) => patch({ vehiculo: { ...state.vehiculo, descripcion: clip(value, VEHICULO_MAX) } }),
        vehiculoModelo: (value: string) => patch({ vehiculo: { ...state.vehiculo, modelo: value.replace(/\D/g, '').slice(0, 4) } }),
    }

    const contratanteCreated = (contratante: QuoteContratante | null) => {
        setNewContratante(null)
        if (contratante) changeContratante(contratante)
    }

    const fields = ramoFields(state.ramo)

    const allErr = useMemo(() => [0, 1, 2].map((sec) => sectionErrors(state, sec)), [state])
    const shownErr = (i: number) => ((tried || triedSec[i]) ? allErr[i] : {})
    const se: FieldErrors = { ...shownErr(0), ...shownErr(1), ...shownErr(2) }
    const errorCount = (i: number) => Object.keys(allErr[i]).length
    const okSec = (i: number) => errorCount(i) === 0
    const errList = tried ? [...Object.values(allErr[0]), ...Object.values(allErr[1])] : []
    const stepMissing = (tried || triedSec[step]) ? Object.values(allErr[step] || {}) : []
    const steps = quoteSteps(state.ramo)
    const issuesByItem = state.items.map((row, index) => itemIssues(row, index, state.ramo))

    const goTo = (next: number) => {
        setStep(next)
        setMaxStep((current) => Math.max(current, next))
    }

    const openProposal = (index: number) => {
        const row = state.items[index]
        if (!row) return
        goTo(LAST_STEP)
        setOpenKey(row.key)
        const issues = Object.keys(issuesByItem[index] || {})
        const tab: ProposalTab | null = issues.some((key) => key.startsWith('p') || key.endsWith('planes'))
            ? 'planes'
            : issues.some((key) => key.endsWith('caracteristicas')) ? 'caracteristicas' : issues.some((key) => key.endsWith('coberturas')) ? 'coberturas' : null
        if (tab) setTabs((current) => ({ ...current, [row.key]: tab }))
    }

    const save = async () => {
        const first = allErr.findIndex((e) => Object.keys(e).length > 0)
        if (first >= 0) {
            setTried(true)
            if (first === LAST_STEP) {
                const bad = issuesByItem.findIndex((issues) => Object.keys(issues).length > 0)
                if (bad >= 0) openProposal(bad)
                else goTo(first)
            } else {
                goTo(first)
            }
            return
        }
        if (!state.contratante) return
        try {
            setLoading(true)
            setMessage(null)
            const request: CreateCotizacionRequest = {
                id: state.id,
                ramo: state.ramo,
                clienteId: state.contratante.tipo === 'cliente' ? state.contratante.id : null,
                prospectoId: state.contratante.tipo === 'prospecto' ? state.contratante.id : null,
                correo: state.correo.trim(),
                status: state.status,
                fecVencimiento: state.fecVencimiento,
                texto: fields.bien ? state.texto.trim() : null,
                vehiculos: fields.vehiculo
                    ? [{ id: state.vehiculo.id, descripcion: state.vehiculo.descripcion.trim(), modelo: Number(state.vehiculo.modelo) }]
                    : null,
                asegurados: fields.asegurados
                    ? state.asegurados.map((row) => ({
                        id: row.id,
                        nombre: row.nombre.trim(),
                        parentesco: row.parentesco as Parentesco,
                        genero: row.genero as Genero,
                        fecNacimiento: row.fecNacimiento || null,
                        edad: row.edad ? Number(row.edad) : null,
                    }))
                    : [],
                items: state.items.map((row) => toItemRequest(row, state.ramo, state.moneda)),
            }
            const response: WrapperResponse<CotizacionDTO> = state.id
                ? await (await APIInvoke.invokePUT(`/cotizaciones/${state.id}`, request)).json()
                : await (await APIInvoke.invokePOST('/cotizaciones', request)).json()

            if (response.ok && response.body) {
                const nextState: QuoteLocationState = { detailId: response.body.id, backUrl: QUOTES_PATH }
                history.push({ pathname: QUOTES_PATH, state: nextState })
            } else {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
            }
        } catch (error) {
            console.error('error al guardar la cotización', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setLoading(false)
        }
    }

    const stepNext = () => {
        if (errorCount(step) > 0) {
            setTriedSec((current) => ({ ...current, [step]: true }))
            return
        }
        goTo(Math.min(step + 1, LAST_STEP))
    }

    const isEdit = Boolean(cotizacionId || state.id)
    const contratanteNombre = state.contratante ? lookupName(state.contratante) : ''
    const moneda = state.moneda

    const proposals = state.items.map((row, index) => {
        const issues = Object.values(issuesByItem[index])
        const desde = cheapestPlan(row)
        const suma = parseAmount(row.sumaAsegurada)
        const tab = tabs[row.key] || 'coberturas'
        const existing = new Set(row.coberturas.map((value) => value.descripcion.trim().toLowerCase()))
        const copyFrom = state.items.findIndex((other) => other.key !== row.key && other.coberturas.some((value) => value.descripcion.trim()))
        return {
            key: row.key,
            n: index + 1,
            title: row.aseguradora ? `${aseguradoraLabel(row.aseguradora)} · ${row.plan.trim() || 'sin plan'}` : 'Nueva propuesta',
            sub: [
                `Suma ${suma ? quoteMoney(suma, moneda) : '—'}`,
                `${row.coberturas.length} coberturas`,
                fields.caracteristicas ? `${row.caracteristicas.length} características` : '',
                `${row.planesPago.length} plan(es) de pago`,
            ].filter(Boolean).join(' · '),
            desde: desde != null ? quoteMoney(desde, moneda) : '—',
            ok: issues.length === 0,
            status: issues.length === 0 ? 'Completa' : `Falta ${issues[issues.length - 1]}`,
            issues,
            open: openKey === row.key,
            tab: tab === 'caracteristicas' && !fields.caracteristicas ? 'coberturas' as ProposalTab : tab,
            tabs: [
                { id: 'coberturas' as ProposalTab, label: 'Coberturas', count: row.coberturas.length, alert: false },
                ...(fields.caracteristicas ? [{ id: 'caracteristicas' as ProposalTab, label: 'Características', count: row.caracteristicas.length, alert: false }] : []),
                { id: 'planes' as ProposalTab, label: 'Planes de pago', count: row.planesPago.length, alert: row.planesPago.length === 0 },
            ],
            suggestions: coberturaSuggestions.filter((value) => !existing.has(value.trim().toLowerCase())).slice(0, SUGGESTION_LIMIT),
            copyFrom: copyFrom >= 0 ? { key: state.items[copyFrom].key, n: copyFrom + 1 } : null,
            planWarning: planWarning(row, moneda),
            planSummaries: row.planesPago.map((plan) => planSummary(plan, moneda)),
        }
    })

    const minimos = state.items.map(cheapestPlan).filter((value): value is number => value != null)
    const planCount = state.items.reduce((acc, row) => acc + row.planesPago.length, 0)
    const pendientes = proposals.filter((row) => !row.ok)
    const openIndex = state.items.findIndex((row) => row.key === openKey)

    return {
        form: state,
        fields,
        planFields: planValueFields(state.ramo),
        aseguradoras,
        monedas,
        templates,
        correoOptions,
        loading,
        busyLabel: cotizacionId && !state.id ? 'Cargando cotización…' : 'Guardando cotización…',
        message,
        title: isEdit ? `Editar cotización${state.noCotizacion ? ` ${state.noCotizacion}` : ''}` : 'Nueva cotización',
        isEdit,
        backTo: backUrl,
        minVencimiento: state.id ? undefined : todayIso(),
        contratanteLookup,
        contratanteMenu,
        newContratante,
        change,
        asegurado,
        item,
        planPago,
        parcialidades,
        drag,
        proposals,
        openIndex,
        openContratanteMenu: (event: React.MouseEvent<HTMLElement>) => setContratanteMenu(event.currentTarget),
        closeContratanteMenu: () => setContratanteMenu(null),
        openNewContratante: (tipo: ContratanteTipo) => {
            setContratanteMenu(null)
            setNewContratante(tipo)
        },
        closeNewContratante: () => setNewContratante(null),
        clienteCreated: (cliente?: ClienteDTO | null) => contratanteCreated(cliente ? contratanteFromCliente(cliente) : null),
        prospectoCreated: (prospecto?: ProspectoDTO | null) => contratanteCreated(prospecto ? contratanteFromProspecto(prospecto) : null),
        save,
        cancel: () => history.push(backUrl),
        step,
        maxStep,
        steps,
        se,
        okSec,
        errList,
        stepMissing,
        stepNext,
        stepBack: () => setStep((current) => Math.max(current - 1, 0)),
        goToStep: (i: number) => { if (i <= maxStep) setStep(i) },
        openProposal,
        toast,
        undo: () => {
            const snapshot = toast?.undo
            if (!snapshot) return
            setState((current) => ({ ...current, ...snapshot }))
            setToast(null)
        },
        previewHref: state.uuid ? `${QUOTE_VIEWER_PATH}/${state.uuid}` : null,
        resumen: {
            ramo: state.ramo,
            numero: state.noCotizacion,
            contratante: contratanteNombre,
            correo: state.correo.trim(),
            vence: formatSpokenDate(state.fecVencimiento) || '—',
            desde: minimos.length ? quoteMoney(Math.min(...minimos), moneda) : '—',
            conteo: `${state.items.length} propuesta(s) · ${planCount} plan(es) de pago`,
            pendientes: tried ? pendientes.map((row) => `Propuesta ${row.n}: falta ${row.issues.join(', ')}`) : [],
            tried,
        },
    }
}
